import Styles from './Header.module.scss'
import { ArrowLeft, Calendar1 } from 'blaze-ui/icons'
import { ChevronLeft, ChevronRight } from 'blaze-ui/icons/24'
import { generatePath, useRouteMatch, useHistory } from 'react-router-dom'
import { SCHEDULED_POSTS_PATH } from 'paths'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import cx from 'classnames'
import { useQueryClient } from 'react-query'
import { removeItemFromCalendarCache } from 'hooks/queries/calendar/utils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { scheduledPostsKeys } from 'hooks/queries/scheduledPosts/scheduledPostsKeys'
import { ProfileDropdown } from 'blaze-components/TopBar/ProfileDropdown/ProfileDropdown'
import { VirtualMarketerUpgradeButton } from 'blaze-components/VirtualMarketerUpgradeButton/VirtualMarketerUpgradeButton'
import { AutopilotProUpgradeButton } from 'components/AutopilotUpgradeButtons/AutopilotProUpgradeButton'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { CreditUsageDropdown } from 'blaze-components/CreditUsageDropdown/CreditUsageDropdown'
import { StatusPill } from './StatusPill'
import { ConnectAccountsButton } from './ConnectAccountsButton'
import { useAuth } from 'hooks/UseAuth'
import { PostActionsButton } from '../PostActionsButton'
import { isAnyTargetProcessing } from '../Header'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { optimisticRemoveFilesFromInfiniteLists } from 'hooks/queries/files/cacheUtils'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { ContentTypeIcon } from './ContentTypeIcon'
import { DesignChannel } from 'components/NewFileModal/Types'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useApprovalActions } from 'hooks/queries/approvals/useApprovalActions'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { Pressable, useHover } from '@react-aria/interactions'
import { PostingOnMenu } from '../Toolbar/PostingOnMenu'
import { CalendarFilters } from 'screens/Calendar/types'
import { getCrossPostGroupForChannel } from '../utils'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { useMemo } from 'react'

const EMPTY_HOVER_OPTS = {}

interface HeaderProps {
  almanacFile?: AlmanacFileShowItem
  onClose: () => void
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void
  onDelete: (skipNavigation?: boolean) => void
  goToPreviousFile?: () => void
  goToNextFile?: () => void
  onUpdateAlmanacFile?: (updatedFile: AlmanacFileShowItem) => void
  channel?: ChannelType | DesignChannel | GenerationType
  filters?: CalendarFilters
}

export const Header = ({
  almanacFile,
  onClose,
  isLoading,
  setIsLoading,
  onDelete,
  goToPreviousFile,
  goToNextFile,
  onUpdateAlmanacFile,
  channel,
  filters,
}: HeaderProps) => {
  const workspaceId = useCurrentWorkspaceId()
  const currentWorkspace = useCurrentWorkspace()
  const workspaces = useWorkspaces()
  const queryClient = useQueryClient()
  const isOnScheduledPosts = useRouteMatch(SCHEDULED_POSTS_PATH)
  const history = useHistory()
  const auth = useAuth()

  const { handleApprove, handleDontPost, handleMoveToDraft, showReviewButtons, showApprovedDate, approvedDate } =
    useApprovalActions({
      almanacFile,
      onUpdateAlmanacFile,
      onDelete,
      setIsLoading,
    })

  const isDraft = almanacFile?.post_status === PostStatusPropertyValue.Draft && almanacFile.post_at === null
  const isPostApprovalsEnabled = Boolean(currentWorkspace?.post_approval_enabled)
  const showScheduleCTA = isDraft

  const { hoverProps: approvedHoverProps, isHovered: isApprovedHovered } = useHover(EMPTY_HOVER_OPTS)

  const handleCalendarButtonPress = () => {
    onClose()
    if (!isOnScheduledPosts) {
      history.push(generatePath(SCHEDULED_POSTS_PATH, { workspaceId }))
    }
  }

  const handleDelete = (almanacFileKey?: string) => {
    if (!almanacFileKey) return
    onDelete()
    removeItemFromCalendarCache(queryClient, almanacFileKey)
    queryClient.invalidateQueries(calendarKeys.detailAll())
    queryClient.invalidateQueries(calendarKeys.all)
    queryClient.invalidateQueries(scheduledPostsKeys.index(workspaceId))
    optimisticRemoveFilesFromInfiniteLists(queryClient, workspaceId, [almanacFileKey])
  }

  const fileChannel = almanacFile?.channel || channel
  const hasApprovalActions = showReviewButtons || showApprovedDate

  const { data: integrationsData } = useExternalIntegrations()
  const crossPostGroup = useMemo(() => (fileChannel ? getCrossPostGroupForChannel(fileChannel) : []), [fileChannel])
  const accountsToConnectCount = useMemo(() => {
    const connectedProviders = new Set(integrationsData?.external_integrations?.map((i) => i.provider) ?? [])
    const unconnected = crossPostGroup.filter((p) => !connectedProviders.has(p))
    return unconnected.length
  }, [crossPostGroup, integrationsData])

  const showConnectAccountsCTA =
    !showScheduleCTA &&
    !showReviewButtons &&
    crossPostGroup.length > 0 &&
    accountsToConnectCount === crossPostGroup.length

  return (
    <div className={Styles.header}>
      <div className={Styles.left}>
        <div className={Styles.iconButtons}>
          <IconButton aria-label="Back" onPress={onClose} icon={ArrowLeft} variant="secondary" />
          <IconButton aria-label="Calendar" onPress={handleCalendarButtonPress} icon={Calendar1} variant="secondary" />
        </div>
        <div className={Styles.titleGroup}>
          <ContentTypeIcon channel={fileChannel} almanacFile={almanacFile} />
          <div className={Styles.title}>
            <Text variant="secondary" color="var(--dark-90)" lineClamp={1}>
              {almanacFile?.title}
            </Text>
          </div>
          <StatusPill
            postStatus={almanacFile?.post_status}
            isPosting={isAnyTargetProcessing(almanacFile?.scheduled_post_targets || [])}
            isUnscheduled={!almanacFile?.post_at}
            isInReview={!!showReviewButtons}
            scheduledPostTargets={almanacFile?.scheduled_post_targets}
            accountsToConnectCount={accountsToConnectCount}
          />
          <PostActionsButton
            almanacFile={almanacFile}
            isDisabled={isLoading}
            onDelete={() => handleDelete(almanacFile?.key)}
          />
        </div>
      </div>

      <div className={cx(Styles.centerNav, { [Styles.hasActions]: hasApprovalActions })}>
        <Button
          variant="tertiary"
          size="sm"
          frontIcon={ChevronLeft}
          onPress={goToPreviousFile}
          isDisabled={!goToPreviousFile || isLoading}
        >
          Previous
        </Button>

        {showConnectAccountsCTA && <ConnectAccountsButton />}

        {showScheduleCTA && almanacFile && (
          <div className={Styles.approvalActions}>
            <PostingOnMenu
              almanacFile={almanacFile}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              onDelete={onDelete}
              filters={filters ?? {}}
              fileChannel={channel ?? ChannelType.InstagramPosts}
              onUpdateAlmanacFile={onUpdateAlmanacFile}
              reviewAndSchedule={isPostApprovalsEnabled}
              buttonSize="sm"
              trigger={
                <Pressable>
                  <Button
                    className={Styles.approveButton}
                    variant={isPostApprovalsEnabled ? 'green' : 'primary'}
                    size="sm"
                    isDisabled={isLoading}
                  >
                    {isPostApprovalsEnabled ? 'Approve & Schedule' : 'Schedule'}
                  </Button>
                </Pressable>
              }
            />
          </div>
        )}

        {!showScheduleCTA && showReviewButtons && (
          <div className={Styles.approvalActions}>
            <Button variant="secondary" size="sm" onPress={handleDontPost} isDisabled={isLoading}>
              Don't Post
            </Button>
            <Button
              variant="green"
              size="sm"
              className={Styles.approveButton}
              onPress={handleApprove}
              isDisabled={isLoading}
            >
              Approve for Posting
            </Button>
          </div>
        )}

        {showApprovedDate && approvedDate && almanacFile && !showConnectAccountsCTA && (
          <div {...approvedHoverProps} className={Styles.approvedPill}>
            {isApprovedHovered ? (
              <Button variant="tertiary" size="sm" onPress={handleMoveToDraft} isDisabled={isLoading}>
                Move to Draft
              </Button>
            ) : (
              <Text variant="secondary" color="var(--dark-90)" lineClamp={1}>
                Approved on {approvedDate}
              </Text>
            )}
          </div>
        )}

        <Button
          variant="tertiary"
          size="sm"
          endIcon={ChevronRight}
          onPress={goToNextFile}
          isDisabled={!goToNextFile || isLoading}
        >
          Next
        </Button>
      </div>

      <div className={Styles.right}>
        {currentWorkspace?.is_ap_freemium ? (
          <AutopilotProUpgradeButton variant="upgrade" />
        ) : (
          <VirtualMarketerUpgradeButton />
        )}
        <CreditUsageDropdown />
        {auth && <ProfileDropdown selectedWorkspaceId={workspaceId} workspaces={workspaces} />}
      </div>
    </div>
  )
}
